import { useCallback, useEffect, useRef, useState } from 'react';

import formatTime from '@cartwhl/common/utils/formatTime';
import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import Text from '@cartwhl/ui/base/Text';
import CameraIcon from '@cartwhl/ui/icons/CameraIcon';
import Button from '@cartwhl/ui/input/Button';

import { noticesStore } from 'stores';

const CAPTURE_LIMIT = 45; // The amount of seconds the user can record for

export default function MediaCapture({
  close,
  next,
}: {
  close: () => void;
  next: (videoFile: File) => void;
}) {
  const [isRecording, setIsRecording] = useState(false);
  const [time, setTime] = useState(0);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [availableCameras, setAvailableCameras] = useState<string[]>([]);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const captureVideo = useRef<HTMLVideoElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const beginRecording = () => {
    if (!streamRef.current) return;

    setTime(0);

    const mediaRecorder = new MediaRecorder(streamRef.current);
    const chunks: BlobPart[] = [];

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunks.push(event.data);
      }
    };

    mediaRecorder.onstop = () => {
      setIsRecording(false);
      mediaRecorderRef.current = null;

      // assuming that it's a webm *may* cause problems, need to test multiple devices to try it out.
      const blob = new Blob(chunks, { type: 'video/webm' });
      next(new File([blob], 'video.webm', { type: 'video/webm' }));
    };

    mediaRecorder.start();
    mediaRecorderRef.current = mediaRecorder;

    intervalRef.current = setInterval(() => {
      setTime((prevTime) => {
        const newTime = prevTime + 1;
        if (newTime >= CAPTURE_LIMIT) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }

          noticesStore.info(
            `You can only record up to ${CAPTURE_LIMIT} seconds`,
            10000,
          );
          stopRecording(newTime);
        }
        return newTime;
      });
    }, 1000);

    setIsRecording(true);
  };

  const stopRecording = (recordedTime = time) => {
    if (recordedTime < 1) return; // Minimum video duration is 1 second

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    const mediaRecorder = mediaRecorderRef.current;
    if (!mediaRecorder || mediaRecorder.state === 'inactive') {
      setIsRecording(false);
      return;
    }

    mediaRecorder.stop();
  };

  const loadCameraDevices = async () => {
    try {
      // Request permission first
      const permissionStream = await navigator.mediaDevices.getUserMedia({
        video: true,
      });
      // Stop the permission stream immediately
      permissionStream.getTracks().forEach((track) => track.stop());

      const devices = await navigator.mediaDevices.enumerateDevices();
      console.log('Devices found:', devices);

      const videoDevices = devices.filter(
        (device) => device.kind === 'videoinput' && device.deviceId,
      );
      const mappedDevices = videoDevices.map((device, index) => ({
        ...device,
        deviceId: device.deviceId,
        label: device.label || `Camera ${index + 1}`,
      }));

      setDevices(mappedDevices);
    } catch (err) {
      console.error('Error loading camera devices:', err);
      noticesStore.error('Unable to access camera devices');
    }
  };

  // Helper function to stop current stream
  const stopCurrentStream = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => {
        track.stop();
      });
      streamRef.current = null;
    }
  };

  // Helper function to set up video stream
  const setupVideoStream = async (stream: MediaStream) => {
    if (captureVideo.current) {
      captureVideo.current.srcObject = stream;
      await captureVideo.current.play();
      streamRef.current = stream;
    }
  };

  // Helper function to detect browser and platform capabilities
  const getBrowserInfo = () => {
    const userAgent = navigator.userAgent;
    const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
    const isFirefox = /Firefox/.test(userAgent);
    const isChrome = /Chrome/.test(userAgent);
    const isMobile =
      /Mobi|Android/i.test(userAgent) || 'ontouchstart' in window;

    return { isSafari, isFirefox, isChrome, isMobile };
  };

  // Helper function to test if facing modes actually work (not just accepted)
  const testFacingModeReality = async (
    facingMode: 'user' | 'environment',
    deviceId?: string,
  ): Promise<boolean> => {
    try {
      const constraints = {
        video: {
          facingMode: { exact: facingMode },
          ...(deviceId && { deviceId: { exact: deviceId } }),
        },
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      const track = stream.getVideoTracks()[0];
      const settings = track.getSettings();

      // Clean up immediately
      stream.getTracks().forEach((track) => track.stop());

      // Check if the browser actually applied the facing mode
      // Some browsers accept the constraint but don't actually use it
      if (settings.facingMode === facingMode) {
        return true;
      }

      // For desktop cameras, facingMode might be undefined even if constraint was accepted
      // This is especially common in Safari on macOS
      if (!settings.facingMode) {
        console.log(
          `Browser accepted ${facingMode} constraint but didn't apply facingMode in settings`,
        );
        return false;
      }

      return false;
    } catch (error) {
      return false;
    }
  };

  // Replace your existing getAvailableFacingModes function with this:
  const getAvailableFacingModes = async (
    deviceId?: string,
  ): Promise<string[]> => {
    const availableFacingModes: string[] = [];
    const { isSafari, isFirefox, isMobile } = getBrowserInfo();

    // On desktop, facing modes usually don't work reliably
    if (!isMobile) {
      console.log(
        'Desktop detected, most facing modes are unreliable on desktop cameras',
      );

      // For Firefox on desktop, don't even try - it correctly rejects them
      if (isFirefox) {
        console.log(
          'Firefox on desktop: skipping facing mode tests (correctly unsupported)',
        );
        return [];
      }

      // For Safari on desktop, test but be more strict about validation
      if (isSafari) {
        console.log(
          'Safari on desktop: testing facing modes with strict validation',
        );
      }
    }

    // Test user facing mode
    const userWorks = await testFacingModeReality('user', deviceId);
    if (userWorks) {
      availableFacingModes.push('user');
      console.log(
        `✅ User facing mode verified${deviceId ? ' for device' : ''}`,
      );
    } else {
      console.log(
        `❌ User facing mode not working${deviceId ? ' for device' : ''}`,
      );
    }

    // Test environment facing mode
    const environmentWorks = await testFacingModeReality(
      'environment',
      deviceId,
    );
    if (environmentWorks) {
      availableFacingModes.push('environment');
      console.log(
        `✅ Environment facing mode verified${deviceId ? ' for device' : ''}`,
      );
    } else {
      console.log(
        `❌ Environment facing mode not working${deviceId ? ' for device' : ''}`,
      );
    }

    // Additional validation: if we're on desktop and both modes "work",
    // it's likely Safari giving false positives
    if (!isMobile && availableFacingModes.length === 2) {
      console.log(
        '⚠️  Both facing modes detected on desktop - likely false positives, clearing list',
      );
      return [];
    }

    return availableFacingModes;
  };

  // Helper function to create a stream with given constraints
  const createStream = async (
    constraints: MediaStreamConstraints,
  ): Promise<MediaStream> => {
    return await navigator.mediaDevices.getUserMedia(constraints);
  };

  const switchCamera = async () => {
    // Get current camera facing mode and device
    const currentTrack = streamRef.current?.getVideoTracks()[0];
    const currentSettings = currentTrack?.getSettings();
    const currentFacingMode = currentSettings?.facingMode;
    const currentDeviceId = currentSettings?.deviceId;
    // Toggle between user and environment
    const targetFacingMode =
      currentFacingMode === 'user' ? 'environment' : 'user';

    try {
      stopCurrentStream();

      // Try to maintain the same device if possible, otherwise let the browser choose
      const constraints = {
        video: {
          facingMode: { ideal: targetFacingMode },
          ...(currentDeviceId && { deviceId: { ideal: currentDeviceId } }),
          width: { ideal: 1280 },
        },
        audio: false,
      };

      const stream = await createStream(constraints);
      await setupVideoStream(stream);
    } catch (err) {
      console.error('Error switching camera:', err);
      noticesStore.error('Unable to switch camera');

      // Fallback: try without device constraint
      try {
        const fallbackConstraints = {
          video: {
            facingMode: { ideal: targetFacingMode },
            width: { ideal: 1280 },
          },
          audio: false,
        };

        const stream = await createStream(fallbackConstraints);
        await setupVideoStream(stream);
      } catch (fallbackErr) {
        console.error('Error in fallback camera switch:', fallbackErr);
      }
    }
  };

  const switchDevice = async (deviceId: string) => {
    try {
      stopCurrentStream();

      // Try with exact deviceId first
      const constraints: MediaStreamConstraints = {
        video: {
          deviceId: { exact: deviceId },
          width: { ideal: 1280 },
        },
        audio: false,
      };

      let stream: MediaStream;
      try {
        stream = await createStream(constraints);
      } catch (exactErr) {
        console.warn(
          'Exact deviceId failed, trying ideal constraint:',
          exactErr,
        );

        // Fallback to ideal constraint for iOS compatibility
        const fallbackConstraints: MediaStreamConstraints = {
          video: {
            deviceId: { ideal: deviceId },
            width: { ideal: 1280 },
          },
          audio: false,
        };
        stream = await createStream(fallbackConstraints);
      }

      await setupVideoStream(stream);

      // Re-check available facing modes for this new device
      const availableFacingModes = await getAvailableFacingModes(deviceId);
      setAvailableCameras(availableFacingModes);
    } catch (err) {
      console.error('Error switching device:', err);
      noticesStore.error(
        `Unable to switch to selected device: ${(err as Error)?.message || 'Unknown error'}`,
      );

      // Fallback: try to reinitialize with default camera
      try {
        await initializeCamera();
      } catch (fallbackErr) {
        console.error('Error in fallback camera initialization:', fallbackErr);
        noticesStore.error('Unable to reinitialize camera');
      }
    }
  };

  const initializeCamera = useCallback(async () => {
    await loadCameraDevices();

    // Check which facing modes are available on this device
    const availableFacingModes = await getAvailableFacingModes();
    setAvailableCameras(availableFacingModes);

    console.log('Available facing modes:', availableFacingModes);

    try {
      const constraints = {
        video: {
          facingMode: availableFacingModes[0] || 'user',
          width: { ideal: 1280 },
        },
        audio: false,
      };

      const stream = await createStream(constraints);
      await setupVideoStream(stream);
    } catch (err) {
      console.error('Error initializing camera:', err);
      noticesStore.error('Unable to access camera');
    }
  }, []);

  useEffect(() => {
    initializeCamera();

    return stopCurrentStream;
  }, [initializeCamera]);

  return (
    <div className="flex h-full flex-col">
      <div className="bg-gray-15 dark:bg-gray-75 dark:border-gray-60 border-gray-30 relative flex min-h-0 flex-1 flex-col rounded-2xl border">
        <div className="relative min-h-0 flex-1">
          {!isRecording && (
            <Text
              variant="studio/body/medium/sm"
              className="absolute right-2 bottom-2 left-2 rounded-lg bg-black/70 px-4 py-4 text-white sm:w-[334px] sm:px-6 md:top-2 md:bottom-auto"
            >
              <h2 className="text-gray-45">For capturing video:</h2>
              <ul className="list-disc pl-5">
                <li>Include only 1 person</li>
                <li>Include the subjects full body</li>
                <li>Capture will only last {CAPTURE_LIMIT} seconds</li>
                <li>
                  In the next step, you will have to trim the video to under 30
                  seconds
                </li>
              </ul>
            </Text>
          )}
          <video
            key="stream"
            ref={captureVideo}
            playsInline
            muted
            className="bg-gray-30 h-full w-full rounded-t-2xl object-cover md:aspect-1280/720"
          />
        </div>
        <div className="relative flex h-[72px] flex-none items-center justify-center gap-3 px-2 md:h-[85px] md:px-5">
          <div className="w-20"></div>
          <button
            className={`flex h-[52px] w-[52px] flex-none items-center justify-center text-white transition-all ${
              isRecording
                ? 'bg-red rounded-2xl hover:bg-[#e6384c]'
                : 'bg-blue rounded-full hover:bg-[#2E62E4]'
            }`}
            onClick={() => (isRecording ? stopRecording() : beginRecording())}
          >
            {isRecording ? (
              <svg
                width="20"
                height="21"
                viewBox="0 0 20 21"
                fill="white"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M0.261719 17.582V3.49609C0.261719 2.65234 0.507812 1.99219 1 1.51562C1.5 1.03906 2.18359 0.800781 3.05078 0.800781H16.9492C17.8164 0.800781 18.4961 1.03906 18.9883 1.51562C19.4883 1.99219 19.7383 2.65234 19.7383 3.49609V17.582C19.7383 18.418 19.4883 19.0742 18.9883 19.5508C18.4961 20.0352 17.8164 20.2773 16.9492 20.2773H3.05078C2.18359 20.2773 1.5 20.0352 1 19.5508C0.507812 19.0742 0.261719 18.418 0.261719 17.582Z" />
              </svg>
            ) : (
              <CameraIcon className="h-6 w-6" />
            )}
          </button>
          <Text as="div" variant="studio/body/medium/md" className="w-20">
            {isRecording ? formatTime(time) : ''}
          </Text>

          <div className="absolute right-4 flex items-center gap-2">
            {availableCameras.length > 1 && !isRecording && (
              <Tooltip text="Switch camera" inModal>
                <Button
                  size="sm"
                  onClick={switchCamera}
                  icon={
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 22 20"
                      fill="currentColor"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M5.52148 10.2974L6.80811 8.521C6.92432 8.36051 7.0682 8.28304 7.23975 8.28857C7.4113 8.28857 7.55241 8.36605 7.66309 8.521L8.96631 10.2974C9.08252 10.4523 9.11296 10.6073 9.05762 10.7622C9.00781 10.9172 8.8833 10.9946 8.68408 10.9946H7.91211C8.03385 11.4373 8.24414 11.833 8.54297 12.1816C8.8418 12.5303 9.2015 12.807 9.62207 13.0117C10.0482 13.2109 10.513 13.3105 11.0166 13.3105C11.3652 13.3105 11.6834 13.2552 11.9712 13.1445C12.259 13.0339 12.5412 12.8761 12.8179 12.6714C13.0005 12.5441 13.1831 12.4832 13.3657 12.4888C13.5483 12.4943 13.7061 12.5773 13.8389 12.7378C13.9772 12.8983 14.0243 13.0781 13.98 13.2773C13.9357 13.4766 13.814 13.6536 13.6147 13.8086C13.2772 14.0742 12.8815 14.2845 12.4277 14.4395C11.974 14.5944 11.5036 14.6719 11.0166 14.6719C10.264 14.6719 9.57503 14.5169 8.94971 14.207C8.32992 13.8916 7.8125 13.46 7.39746 12.9121C6.98796 12.3587 6.7168 11.7196 6.58398 10.9946H5.79541C5.59619 10.9946 5.47168 10.9199 5.42188 10.7705C5.3776 10.6156 5.41081 10.4578 5.52148 10.2974ZM8.16943 7.71582C8.03662 7.55534 7.98958 7.37826 8.02832 7.18457C8.07259 6.98535 8.1971 6.80827 8.40186 6.65332C8.73389 6.38216 9.12679 6.16911 9.58057 6.01416C10.0343 5.85921 10.5075 5.78174 11 5.78174C11.7471 5.78174 12.4305 5.93945 13.0503 6.25488C13.6701 6.57031 14.1875 7.00472 14.6025 7.55811C15.0176 8.10596 15.2915 8.73958 15.4243 9.45898H16.1714C16.3651 9.45898 16.4868 9.53646 16.5366 9.69141C16.5864 9.84635 16.556 10.0013 16.4453 10.1562L15.167 11.9409C15.0452 12.1014 14.8986 12.1816 14.7271 12.1816C14.561 12.1761 14.4199 12.0959 14.3037 11.9409L13.0088 10.1562C12.8926 10.0013 12.8594 9.84635 12.9092 9.69141C12.959 9.53646 13.0807 9.45898 13.2744 9.45898H14.0962C13.9744 9.01628 13.7642 8.62061 13.4653 8.27197C13.1665 7.92334 12.804 7.64941 12.3779 7.4502C11.9574 7.25098 11.498 7.15137 11 7.15137C10.6403 7.15137 10.3166 7.20394 10.0288 7.30908C9.74105 7.41423 9.46159 7.57194 9.19043 7.78223C9.01335 7.91504 8.8335 7.97591 8.65088 7.96484C8.46826 7.95378 8.30778 7.87077 8.16943 7.71582ZM3.46289 17.7681C2.56641 17.7681 1.88851 17.5384 1.4292 17.0791C0.969889 16.6253 0.740234 15.9557 0.740234 15.0703V6.36279C0.740234 5.47738 0.969889 4.80778 1.4292 4.354C1.88851 3.89469 2.56641 3.66504 3.46289 3.66504H5.6875C6.014 3.66504 6.25749 3.62907 6.41797 3.55713C6.57845 3.47965 6.7583 3.33854 6.95752 3.13379L7.61328 2.41992C7.8291 2.19303 8.06152 2.02425 8.31055 1.91357C8.5651 1.79736 8.90267 1.73926 9.32324 1.73926H12.627C13.0475 1.73926 13.3851 1.79736 13.6396 1.91357C13.8942 2.02425 14.1266 2.19303 14.3369 2.41992L15.001 3.13379C15.1338 3.27214 15.2528 3.38005 15.3579 3.45752C15.4686 3.53499 15.5903 3.59033 15.7231 3.62354C15.8615 3.6512 16.0413 3.66504 16.2627 3.66504H18.5288C19.4253 3.66504 20.1032 3.89469 20.5625 4.354C21.0273 4.80778 21.2598 5.47738 21.2598 6.36279V15.0703C21.2598 15.9557 21.0273 16.6253 20.5625 17.0791C20.1032 17.5384 19.4253 17.7681 18.5288 17.7681H3.46289ZM3.5625 16.1328H18.4292C18.811 16.1328 19.1043 16.0332 19.3091 15.834C19.5194 15.6348 19.6245 15.3332 19.6245 14.9292V6.50391C19.6245 6.09993 19.5194 5.79834 19.3091 5.59912C19.1043 5.3999 18.811 5.30029 18.4292 5.30029H15.7896C15.4132 5.30029 15.1117 5.25879 14.8848 5.17578C14.6634 5.09277 14.4476 4.93783 14.2373 4.71094L13.5981 4.00537C13.3657 3.75081 13.1582 3.58203 12.9756 3.49902C12.793 3.41048 12.519 3.36621 12.1538 3.36621H9.79639C9.43669 3.36621 9.16276 3.41048 8.97461 3.49902C8.79199 3.58203 8.58447 3.75081 8.35205 4.00537L7.72119 4.71094C7.5109 4.93783 7.29232 5.09277 7.06543 5.17578C6.83854 5.25879 6.53971 5.30029 6.16895 5.30029H3.5625C3.18066 5.30029 2.88737 5.3999 2.68262 5.59912C2.47786 5.79834 2.37549 6.09993 2.37549 6.50391V14.9292C2.37549 15.3332 2.47786 15.6348 2.68262 15.834C2.88737 16.0332 3.18066 16.1328 3.5625 16.1328Z" />
                    </svg>
                  }
                />
              </Tooltip>
            )}

            {devices && devices?.length > 1 && (
              <Dropdown
                side="top"
                trigger={<Button size="sm" icon={<DevicesIcon />} />}
              >
                {devices.map((device) => (
                  <Dropdown.Item
                    key={device.deviceId}
                    onClick={() => {
                      switchDevice(device.deviceId);
                    }}
                  >
                    {device.label}
                  </Dropdown.Item>
                ))}
              </Dropdown>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-none justify-between pt-4 md:pt-6">
        <Button onClick={close}>Cancel</Button>
        <Tooltip text="Record a video to continue" inModal>
          <Button variant="primary" disabled>
            Finish
          </Button>
        </Tooltip>
      </div>
    </div>
  );
}

const DevicesIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="32"
      height="32"
      fill="currentColor"
      viewBox="0 0 256 256"
      className={className}
    >
      <path d="M224,72H208V64a24,24,0,0,0-24-24H40A24,24,0,0,0,16,64v96a24,24,0,0,0,24,24H152v8a24,24,0,0,0,24,24h48a24,24,0,0,0,24-24V96A24,24,0,0,0,224,72ZM40,168a8,8,0,0,1-8-8V64a8,8,0,0,1,8-8H184a8,8,0,0,1,8,8v8H176a24,24,0,0,0-24,24v72Zm192,24a8,8,0,0,1-8,8H176a8,8,0,0,1-8-8V96a8,8,0,0,1,8-8h48a8,8,0,0,1,8,8Zm-96,16a8,8,0,0,1-8,8H88a8,8,0,0,1,0-16h40A8,8,0,0,1,136,208Zm80-96a8,8,0,0,1-8,8H192a8,8,0,0,1,0-16h16A8,8,0,0,1,216,112Z" />
    </svg>
  );
};
